import { RELEASES_API_URL, RELEASES_FILTER_URL, RELEASES_VERSION_URL } from 'constants/ApiConstants';
import * as types from 'modules/releases/actionTypes';

import { RELEASE_STATE } from '../constants';

/**
 * @param {Partial<ReleasesQueryParams>} params
 * @param {Object} [meta={}]
 * @returns {FetchReleasesAction}
 */
export const fetchReleases = (params = {}, meta = {}) => ({
  type: types.FETCH_RELEASES,
  payload: {
    request: {
      method: 'GET',
      url: RELEASES_FILTER_URL,
      params,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 * @returns {FetchReleaseByIdAction}
 */
export const fetchReleaseById = (releaseId, meta = {}) => ({
  type: types.FETCH_RELEASE_BY_ID,
  payload: {
    request: {
      method: 'GET',
      url: `${RELEASES_API_URL}/${releaseId}`,
    },
    releaseId,
  },
  meta: {
    '@app-request': true,
    '@add-etag-in-result': true,
    ...meta,
  },
});

/**
 * @param {Release} releaseData
 * @param {Object} [meta={}]
 * @returns {CreateReleaseAction}
 */
export const createRelease = (releaseData, meta = {}) => ({
  type: types.CREATE_RELEASE,
  payload: {
    request: {
      method: 'POST',
      url: RELEASES_API_URL,
      data: releaseData,
    },
  },
  meta
});

/**
 * @param {Release} releaseData
 * @param {Object} [meta={}]
 * @returns {UpdateReleaseAction}
 */
export const updateRelease = (releaseData, meta = {}) => ({
  type: types.UPDATE_RELEASE,
  payload: {
    request: {
      method: 'PUT',
      url: `${RELEASES_API_URL}/${releaseData.id}`,
      data: releaseData,
    },
  },
  meta
});

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 * @returns {DeleteReleaseAction}
 */
export const deleteRelease = (releaseId, meta = {}) => ({
  type: types.DELETE_RELEASE,
  payload: {
    request: {
      method: 'DELETE',
      url: `${RELEASES_API_URL}/${releaseId}`,
    },
    releaseId,
  },
  meta
});

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 * @returns {DeleteReleaseSuccessAction}
 */
export const deleteReleaseSuccess = (releaseId, meta = {}) => ({
  type: types.DELETE_RELEASE_SUCCESS,
  payload: releaseId,
  meta
});

/**
 * @param {string} releaseId
 * @param {import('../constants').RELEASE_STATE} newState
 * @param {Object} [meta={}]
 * @returns {PatchReleaseAction}
 */
export const patchReleaseState = (releaseId, newState, meta = {}) => ({
  type: types.PATCH_RELEASE_STATE,
  payload: {
    request: {
      method: 'PATCH',
      url: `${RELEASES_API_URL}/${releaseId}`,
      data: { changeStateTo: newState },
    },
    releaseId,
  },
  meta
});

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 */
export const publishRelease = (releaseId, meta = {}) =>
  patchReleaseState(releaseId, RELEASE_STATE.PUBLISHED, meta);

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 */
export const archiveRelease = (releaseId, meta = {}) =>
  patchReleaseState(releaseId, RELEASE_STATE.ARCHIVED, meta);

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 */
export const unarchiveRelease = (releaseId, meta = {}) =>
  patchReleaseState(releaseId, RELEASE_STATE.DRAFT, meta);

/**
 * @param {string} releaseId
 * @param {Object} [meta={}]
 */
export const unpublishRelease = (releaseId, meta = {}) =>
  patchReleaseState(releaseId, RELEASE_STATE.DRAFT, meta);

/**
 * @param {string} version
 * @param {string} releaseType
 * @param {Object} [meta={}]
 * @returns {CheckVersionExistsAction}
 */
export const checkVersionExists = (version, releaseType, meta = {}) => ({
  type: types.CHECK_VERSION_EXISTS,
  payload: {
    request: {
      method: 'HEAD',
      url: RELEASES_VERSION_URL,
      params: { version, type: releaseType },
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  }
});

/**
 * @param {string} releaseType
 * @param {Object} [meta={}]
 * @returns {GetLatestVersionAction}
 */
export const getLatestVersion = (releaseType, meta = {}) => ({
  type: types.GET_LATEST_VERSION,
  payload: {
    request: {
      method: 'GET',
      url: RELEASES_FILTER_URL,
      params: { releaseTypes: [releaseType], sort: 'releaseVersionArray,desc', size: 1 },
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  }
});
